import {
  Box,
  Button,
  Container,
  Grid,
  keyframes,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  typographyClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { Maintenance } from '../../components/room/types';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { StyledPaperItem } from '../../common/mui-components';
import FlareIcon from '@mui/icons-material/Flare';
import DiamondIcon from '@mui/icons-material/Diamond';
import { SoloPunchcardDetailsDialog } from '../../dialog/SoloPunchcardDetailsDialog';
import { TbAtom } from 'react-icons/tb';
import RocketIcon from '@mui/icons-material/Rocket';
import { MEDAL_COLORS, MEDAL_REQUIREMENTS } from '@par/shared/common/constants';
import {
  getSoloStat,
  getSoloStatValue,
} from '@par/shared/components/punchcard';
import {
  CategorySet,
  GameType,
  MedalCategory,
  PunchcardCategory,
} from '@par/shared/common/types';
import { Profile } from '@par/shared/types/player';
import InfoIcon from '@mui/icons-material/Info';
import { SoloPunchcardInfoDialog } from '../../dialog/SoloPunchcardInfoDialog';
import { fetchProfileById } from '@par/shared/components/profile';
import { useParams } from 'react-router-dom';

const shine = keyframes`
  0% {
    background-position: -100px;
  }
  100% {
    background-position: 100px;
  }
`;

const ShinyTypography = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: {
    animation: `${shine} 4s infinite`,
    backgroundClip: 'text',
    backgroundImage:
      'linear-gradient(-40deg, transparent 0%, transparent 40%, #fff 50%, transparent 60%, transparent 100%)',
    backgroundRepeat: 'no-repeat',
  },
}));

const labelColorsLight = {
  average: '#B0E0E6',
  best: '#00FF00',
  gamesPlayed: '#EE82EE',
};

const labelColorsDark = {
  average: '#43B5C4',
  best: '#00C800',
  gamesPlayed: '#E43CE4',
};

const ModalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  bgcolor: 'background.default',
  border: '1px solid #fff',
  boxShadow: 24,
  p: 4,
};

const CenteredTypography = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: {
    position: 'relative',
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

const CenteredStack = styled(Stack)(() => ({
  position: 'relative',
  top: '50%',
  transform: 'translateY(-50%)',
  justifyContent: 'center',
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    border: 'none',
  },
}));

const StyledCenteredTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    border: 'none',
    textAlign: 'center',
  },
}));

type PropTypes = {
  maintenance: Maintenance;
  onPlaySoloFromPunchcard: (
    gameType: GameType,
    categorySet: string,
    customGameName?: string
  ) => void;
  myProfileId: string;
};

export const SoloPunchcard = ({
  maintenance,
  onPlaySoloFromPunchcard,
  myProfileId,
}: PropTypes) => {
  const { profileId } = useParams();
  const [viewProfile, setViewProfile] = useState<Profile>();
  const [clickedGameType, setClickedGameType] = useState<GameType>();
  const [clickedCategorySet, setClickedCategorySet] = useState<string>();
  const [openPlaySoloModal, setOpenPlaySoloModal] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!profileId) return;
    fetchProfilePageData(profileId);
  }, [profileId]);

  const fetchProfilePageData = async (profileId: string) => {
    const profileRes: Profile = await fetchProfileById(profileId);
    setViewProfile(profileRes);
  };

  const getMedalColor = (
    gameType: GameType,
    categorySet: CategorySet,
    punchcardCategory: PunchcardCategory
  ) => {
    if (!viewProfile) return 'black';
    const { soloStats } = viewProfile;
    if (
      punchcardCategory === 'bestAverage' &&
      soloStats[gameType][categorySet]['gamesPlayed'] < 10
    )
      return 'black';
    const stat = soloStats[gameType][categorySet][punchcardCategory];
    for (const key of Object.keys(
      MEDAL_REQUIREMENTS[punchcardCategory]
    ).reverse()) {
      if (
        punchcardCategory === 'bestAverage' &&
        stat <= MEDAL_REQUIREMENTS[punchcardCategory][key as MedalCategory]
      ) {
        return MEDAL_COLORS[key as MedalCategory];
      }
      if (
        punchcardCategory === 'best' &&
        stat <= MEDAL_REQUIREMENTS[punchcardCategory][key as MedalCategory]
      ) {
        return MEDAL_COLORS[key as MedalCategory];
      }
      if (
        punchcardCategory === 'gamesPlayed' &&
        stat >= MEDAL_REQUIREMENTS[punchcardCategory][key as MedalCategory]
      ) {
        return MEDAL_COLORS[key as MedalCategory];
      }
    }
    return 'black';
  };

  const getIcon = (
    gameType: GameType,
    categorySet: CategorySet,
    punchcardCategory: PunchcardCategory,
    value?: number
  ) => {
    if (value === undefined || value === null)
      return (
        <EmojiEventsIcon
          sx={{
            visibility: 'hidden',
          }}
        />
      );
    const avgGamesPlayed = viewProfile
      ? viewProfile.soloStats[gameType][categorySet]['gamesPlayed']
      : 0;
    for (const key of Object.keys(
      MEDAL_REQUIREMENTS[punchcardCategory]
    ).reverse()) {
      if (
        punchcardCategory === 'bestAverage' &&
        avgGamesPlayed >= 10 &&
        value <= MEDAL_REQUIREMENTS[punchcardCategory][key as MedalCategory]
      ) {
        let icon = (
          <EmojiEventsIcon
            sx={{
              color: getMedalColor(gameType, categorySet, punchcardCategory),
              visibility:
                getMedalColor(gameType, categorySet, punchcardCategory) ===
                'black'
                  ? 'hidden'
                  : 'visible',
            }}
          />
        );
        if (key === 'antimatter') {
          icon = (
            <TbAtom
              size={'1.5rem'}
              color={'#cbcbcb'}
              style={{ boxShadow: '1px 1px 15px #FFF, 1px 1px 5px #fffefe' }}
            />
          );
        }
        if (key === 'unobtainium') {
          icon = <RocketIcon sx={{ color: 'red' }} />;
        }
        if (key === 'diamond') {
          icon = (
            <DiamondIcon
              sx={{
                color: getMedalColor(gameType, categorySet, punchcardCategory),
              }}
            />
          );
        }
        if (key === 'platinum') {
          icon = (
            <FlareIcon
              sx={{
                color: getMedalColor(gameType, categorySet, punchcardCategory),
              }}
            />
          );
        }
        return (
          <Tooltip
            title={`Historical best 10 game average: ${value}`}
            placement="top"
          >
            {icon}
          </Tooltip>
        );
      }
      if (
        punchcardCategory === 'best' &&
        value <= MEDAL_REQUIREMENTS[punchcardCategory][key as MedalCategory]
      ) {
        if (key === 'antimatter') {
          return (
            <TbAtom
              size={'1.5rem'}
              color={'#cbcbcb'}
              style={{ boxShadow: '1px 1px 15px #FFF, 1px 1px 5px #fffefe' }}
            />
          );
        }
        if (key === 'unobtainium') {
          return <RocketIcon sx={{ color: 'red' }} />;
        }
        if (key === 'diamond') {
          return (
            <DiamondIcon
              sx={{
                color: getMedalColor(gameType, categorySet, punchcardCategory),
              }}
            />
          );
        }
        if (key === 'platinum') {
          return (
            <FlareIcon
              sx={{
                color: getMedalColor(gameType, categorySet, punchcardCategory),
              }}
            />
          );
        }
        return (
          <EmojiEventsIcon
            sx={{
              color: getMedalColor(gameType, categorySet, punchcardCategory),
              visibility:
                getMedalColor(gameType, categorySet, punchcardCategory) ===
                'black'
                  ? 'hidden'
                  : 'visible',
            }}
          />
        );
      }
      if (
        punchcardCategory === 'gamesPlayed' &&
        value >= MEDAL_REQUIREMENTS[punchcardCategory][key as MedalCategory]
      ) {
        if (key === 'antimatter') {
          return (
            <TbAtom
              size={'1.5rem'}
              color={'#cbcbcb'}
              style={{ boxShadow: '1px 1px 15px #FFF, 1px 1px 5px #fffefe' }}
            />
          );
        }
        if (key === 'unobtainium') {
          return <RocketIcon sx={{ color: 'red' }} />;
        }
        if (key === 'diamond') {
          return (
            <DiamondIcon
              sx={{
                color: getMedalColor(gameType, categorySet, punchcardCategory),
              }}
            />
          );
        }
        if (key === 'platinum') {
          return (
            <FlareIcon
              sx={{
                color: getMedalColor(gameType, categorySet, punchcardCategory),
              }}
            />
          );
        }
        return (
          <EmojiEventsIcon
            sx={{
              color: getMedalColor(gameType, categorySet, punchcardCategory),
              visibility:
                getMedalColor(gameType, categorySet, punchcardCategory) ===
                'black'
                  ? 'hidden'
                  : 'visible',
            }}
          />
        );
      }
    }
    return (
      <EmojiEventsIcon
        sx={{
          color: getMedalColor(gameType, categorySet, punchcardCategory),
          visibility:
            getMedalColor(gameType, categorySet, punchcardCategory) === 'black'
              ? 'hidden'
              : 'visible',
        }}
      />
    );
  };

  const labelColors =
    theme.palette.mode === 'dark' ? labelColorsLight : labelColorsDark;

  const handleClickPlaySolo = (gameType: GameType, categorySet: string) => {
    setClickedGameType(gameType);
    setClickedCategorySet(categorySet);
    setOpenPlaySoloModal(true);
  };

  const horizontalPunchcard = (
    <Grid container spacing={2} mt={2}>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredStack spacing={0.5}>
            <Typography color={labelColors['average']}>Average</Typography>
            <Typography color={labelColors['best']}>Best</Typography>
            <Typography color={labelColors['gamesPlayed']}>
              Games Played
            </Typography>
          </CenteredStack>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>All</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Pop Culture</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Humanities</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Hard & Soft Sciences</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Fine Arts</CenteredTypography>
        </StyledPaperItem>
      </Grid>

      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Casual</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('casual', 'all')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'all', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'all', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('casual', 'all', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'all',
                  'bestAverage',
                  getSoloStatValue('casual', 'all', 'bestAverage', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'all',
                  'best',
                  getSoloStatValue('casual', 'all', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'all',
                  'gamesPlayed',
                  getSoloStatValue('casual', 'all', 'gamesPlayed', viewProfile)
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('casual', 'popCulture')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'popCulture', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'popCulture', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'popCulture',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'popCulture',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'popCulture',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'popCulture',
                  'best',
                  getSoloStatValue('casual', 'popCulture', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'popCulture',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'popCulture',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('casual', 'humanities')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'humanities', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'humanities', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'humanities',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'humanities',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'humanities',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'humanities',
                  'best',
                  getSoloStatValue('casual', 'humanities', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'humanities',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'humanities',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('casual', 'hardSoftSciences')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat(
                    'casual',
                    'hardSoftSciences',
                    'average',
                    viewProfile
                  )}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat(
                    'casual',
                    'hardSoftSciences',
                    'best',
                    viewProfile
                  )}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'hardSoftSciences',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'hardSoftSciences',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'hardSoftSciences',
                  'best',
                  getSoloStatValue(
                    'casual',
                    'hardSoftSciences',
                    'best',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'hardSoftSciences',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('casual', 'fineArts')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'fineArts', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'fineArts', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'fineArts',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'fineArts',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'fineArts',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'fineArts',
                  'best',
                  getSoloStatValue('casual', 'fineArts', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'fineArts',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'fineArts',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Pro</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('pro', 'all')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'all', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'all', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'all', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'all',
                  'bestAverage',
                  getSoloStatValue('pro', 'all', 'bestAverage', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'all',
                  'best',
                  getSoloStatValue('pro', 'all', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'all',
                  'gamesPlayed',
                  getSoloStatValue('pro', 'all', 'gamesPlayed', viewProfile)
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('pro', 'popCulture')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'popCulture', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'popCulture', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'popCulture', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'popCulture',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'popCulture',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'popCulture',
                  'best',
                  getSoloStatValue('pro', 'popCulture', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'popCulture',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'popCulture',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('pro', 'humanities')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'humanities', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'humanities', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'humanities', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'humanities',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'humanities',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'humanities',
                  'best',
                  getSoloStatValue('pro', 'humanities', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'humanities',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'humanities',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('pro', 'hardSoftSciences')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat(
                    'pro',
                    'hardSoftSciences',
                    'average',
                    viewProfile
                  )}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'hardSoftSciences', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'pro',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'hardSoftSciences',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'hardSoftSciences',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'hardSoftSciences',
                  'best',
                  getSoloStatValue(
                    'pro',
                    'hardSoftSciences',
                    'best',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'hardSoftSciences',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={2}>
        <StyledPaperItem elevation={3} sx={{ height: '120px' }}>
          <Button
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => handleClickPlaySolo('pro', 'fineArts')}
          >
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'fineArts', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'fineArts', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'fineArts', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'fineArts',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'fineArts',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'fineArts',
                  'best',
                  getSoloStatValue('pro', 'fineArts', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'fineArts',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'fineArts',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
    </Grid>
  );

  const verticalPunchcard = (
    <Grid container spacing={{ xs: 1, sm: 2 }} mt={2}>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredStack spacing={0.5}>
            <Typography color={labelColors['average']}>Average</Typography>
            <Typography color={labelColors['best']}>Best</Typography>
            <Typography color={labelColors['gamesPlayed']}>
              Games Played
            </Typography>
          </CenteredStack>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Casual</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Pro</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>All</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'all', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'all', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('casual', 'all', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'all',
                  'bestAverage',
                  getSoloStatValue('casual', 'all', 'bestAverage', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'all',
                  'best',
                  getSoloStatValue('casual', 'all', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'all',
                  'gamesPlayed',
                  getSoloStatValue('casual', 'all', 'gamesPlayed', viewProfile)
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'all', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'all', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'all', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'all',
                  'bestAverage',
                  getSoloStatValue('pro', 'all', 'bestAverage', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'all',
                  'best',
                  getSoloStatValue('pro', 'all', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'all',
                  'gamesPlayed',
                  getSoloStatValue('pro', 'all', 'gamesPlayed', viewProfile)
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Pop Culture</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'popCulture', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'popCulture', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'popCulture',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'popCulture',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'popCulture',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'popCulture',
                  'best',
                  getSoloStatValue('casual', 'popCulture', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'popCulture',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'popCulture',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'popCulture', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'popCulture', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'popCulture', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'popCulture',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'popCulture',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'popCulture',
                  'best',
                  getSoloStatValue('pro', 'popCulture', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'popCulture',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'popCulture',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Humanities</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'humanities', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'humanities', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'humanities',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'humanities',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'humanities',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'humanities',
                  'best',
                  getSoloStatValue('casual', 'humanities', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'humanities',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'humanities',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'humanities', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'humanities', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'humanities', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'humanities',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'humanities',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'humanities',
                  'best',
                  getSoloStatValue('pro', 'humanities', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'humanities',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'humanities',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Hard & Soft Sciences</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat(
                    'casual',
                    'hardSoftSciences',
                    'average',
                    viewProfile
                  )}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat(
                    'casual',
                    'hardSoftSciences',
                    'best',
                    viewProfile
                  )}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'hardSoftSciences',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'hardSoftSciences',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'hardSoftSciences',
                  'best',
                  getSoloStatValue(
                    'casual',
                    'hardSoftSciences',
                    'best',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'hardSoftSciences',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat(
                    'pro',
                    'hardSoftSciences',
                    'average',
                    viewProfile
                  )}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'hardSoftSciences', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'pro',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'hardSoftSciences',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'hardSoftSciences',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'hardSoftSciences',
                  'best',
                  getSoloStatValue(
                    'pro',
                    'hardSoftSciences',
                    'best',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'hardSoftSciences',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'hardSoftSciences',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <CenteredTypography>Fine Arts</CenteredTypography>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('casual', 'fineArts', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('casual', 'fineArts', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat(
                    'casual',
                    'fineArts',
                    'gamesPlayed',
                    viewProfile
                  )}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'casual',
                  'fineArts',
                  'bestAverage',
                  getSoloStatValue(
                    'casual',
                    'fineArts',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'casual',
                  'fineArts',
                  'best',
                  getSoloStatValue('casual', 'fineArts', 'best', viewProfile)
                )}
                {getIcon(
                  'casual',
                  'fineArts',
                  'gamesPlayed',
                  getSoloStatValue(
                    'casual',
                    'fineArts',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
      <Grid item xs={4}>
        <StyledPaperItem sx={{ height: '120px' }}>
          <Button fullWidth sx={{ height: '100%' }}>
            <Stack direction={'row'} spacing={3}>
              <Stack spacing={0.5}>
                <Typography color={labelColors['average']}>
                  {getSoloStat('pro', 'fineArts', 'average', viewProfile)}
                </Typography>
                <Typography color={labelColors['best']}>
                  {getSoloStat('pro', 'fineArts', 'best', viewProfile)}
                </Typography>
                <Typography color={labelColors['gamesPlayed']}>
                  {getSoloStat('pro', 'fineArts', 'gamesPlayed', viewProfile)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {getIcon(
                  'pro',
                  'fineArts',
                  'bestAverage',
                  getSoloStatValue(
                    'pro',
                    'fineArts',
                    'bestAverage',
                    viewProfile
                  )
                )}
                {getIcon(
                  'pro',
                  'fineArts',
                  'best',
                  getSoloStatValue('pro', 'fineArts', 'best', viewProfile)
                )}
                {getIcon(
                  'pro',
                  'fineArts',
                  'gamesPlayed',
                  getSoloStatValue(
                    'pro',
                    'fineArts',
                    'gamesPlayed',
                    viewProfile
                  )
                )}
              </Stack>
            </Stack>
          </Button>
        </StyledPaperItem>
      </Grid>
    </Grid>
  );

  const trophyRequirements = (
    <Box
      sx={{
        maxWidth: '900px',
        border: '1px solid',
        borderColor: '#2a2e34',
        mt: 2,
        padding: 2,
      }}
    >
      <Typography variant="h6" textAlign="center" letterSpacing={2}>
        Trophy Requirements
      </Typography>
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table stickyHeader size="small" aria-label="sticky table">
          <TableHead>
            <TableRow>
              <StyledCenteredTableCell></StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <EmojiEventsIcon
                  sx={{
                    color: MEDAL_COLORS['bronze'],
                    marginBottom: '5px',
                  }}
                />
                <ShinyTypography variant="body1" color={MEDAL_COLORS['bronze']}>
                  Bronze
                </ShinyTypography>
              </StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <EmojiEventsIcon
                  sx={{
                    color: MEDAL_COLORS['silver'],
                    marginBottom: '5px',
                  }}
                />
                <ShinyTypography variant="body1" color={MEDAL_COLORS['silver']}>
                  Silver
                </ShinyTypography>
              </StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <EmojiEventsIcon
                  sx={{
                    color: MEDAL_COLORS['gold'],
                    marginBottom: '5px',
                  }}
                />
                <ShinyTypography variant="body1" color={MEDAL_COLORS['gold']}>
                  Gold
                </ShinyTypography>
              </StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <FlareIcon
                  sx={{
                    color: MEDAL_COLORS['platinum'],
                    marginBottom: '5px',
                  }}
                />
                <ShinyTypography
                  variant="body1"
                  color={MEDAL_COLORS['platinum']}
                >
                  Platinum
                </ShinyTypography>
              </StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <DiamondIcon
                  sx={{
                    color: MEDAL_COLORS['diamond'],
                    marginBottom: '5px',
                  }}
                />
                <ShinyTypography
                  variant="body1"
                  color={MEDAL_COLORS['diamond']}
                >
                  Diamond
                </ShinyTypography>
              </StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <RocketIcon sx={{ color: 'red', marginBottom: '5px' }} />
                <ShinyTypography
                  variant="body1"
                  color={MEDAL_COLORS['unobtainium']}
                >
                  Unobtainium
                </ShinyTypography>
              </StyledCenteredTableCell>
              <StyledCenteredTableCell>
                <TbAtom
                  size={'1.5rem'}
                  color={'#cbcbcb'}
                  style={{
                    boxShadow: '1px 1px 15px #FFF, 1px 1px 5px #fffefe',
                    marginBottom: '5px',
                  }}
                />
                <ShinyTypography
                  variant="body1"
                  color={MEDAL_COLORS['antimatter']}
                  sx={{
                    fontWeight: 600,
                    textShadow: `1px 1px 10px #fff, 1px 1px 10px #fffefe`,
                  }}
                >
                  Antimatter
                </ShinyTypography>
              </StyledCenteredTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <StyledTableCell sx={{ color: labelColors['average'] }}>
                Average
              </StyledTableCell>
              <StyledTableCell>+40</StyledTableCell>
              <StyledTableCell>+30</StyledTableCell>
              <StyledTableCell>+20</StyledTableCell>
              <StyledTableCell>0</StyledTableCell>
              <StyledTableCell>-20</StyledTableCell>
              <StyledTableCell>-40</StyledTableCell>
              <StyledTableCell>-50</StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell sx={{ color: labelColors['best'] }}>
                Best
              </StyledTableCell>
              <StyledTableCell>+20</StyledTableCell>
              <StyledTableCell>+10</StyledTableCell>
              <StyledTableCell>0</StyledTableCell>
              <StyledTableCell>-20</StyledTableCell>
              <StyledTableCell>-40</StyledTableCell>
              <StyledTableCell>-60</StyledTableCell>
              <StyledTableCell>-70</StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell sx={{ color: labelColors['gamesPlayed'] }}>
                Games Played
              </StyledTableCell>
              <StyledTableCell>25</StyledTableCell>
              <StyledTableCell>50</StyledTableCell>
              <StyledTableCell>100</StyledTableCell>
              <StyledTableCell>150</StyledTableCell>
              <StyledTableCell>250</StyledTableCell>
              <StyledTableCell>500</StyledTableCell>
              <StyledTableCell>1000</StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography fontSize={10} textAlign="right" mt={1}>
        * Avg requires min. 10 games and is a rolling average of past 10 games
      </Typography>
      <Typography fontSize={10} textAlign="right" mt={1}>
        * Avg trophy will display your historical best 10 game average. Hover
        the trophy.
      </Typography>
    </Box>
  );

  const mobilePunchcard = (
    <>
      <Typography
        variant="h4"
        textAlign={'center'}
        sx={{ letterSpacing: { xs: 10, sm: 20 } }}
      >
        SOLO PUNCHCARD
      </Typography>
      {verticalPunchcard}
      {trophyRequirements}
    </>
  );

  const [openSoloPunchcardInfo, setOpenSoloPunchcardInfo] = useState(false);

  const punchcard = (
    <>
      <SoloPunchcardInfoDialog
        close={() => setOpenSoloPunchcardInfo(false)}
        open={openSoloPunchcardInfo}
      />
      {clickedCategorySet && clickedGameType ? (
        <SoloPunchcardDetailsDialog
          categorySet={clickedCategorySet}
          close={() => setOpenPlaySoloModal(false)}
          gameType={clickedGameType}
          maintenance={maintenance}
          onPlaySoloFromPunchcard={onPlaySoloFromPunchcard}
          open={openPlaySoloModal}
          profileId={profileId}
          viewProfile={viewProfile}
        ></SoloPunchcardDetailsDialog>
      ) : null}
      <Container sx={{ position: 'relative' }}>
        <Typography variant="h4" textAlign={'center'} letterSpacing={20}>
          <Typography
            variant="h6"
            textAlign={'center'}
            letterSpacing={5}
            mb={1}
          >
            {viewProfile?.username}'s
          </Typography>
          SOLO PUNCHCARD
        </Typography>
        <Button
          startIcon={<InfoIcon />}
          onClick={() => setOpenSoloPunchcardInfo(true)}
          sx={{ position: 'absolute', top: 0, right: 0, color: 'white' }}
        >
          What is this?
        </Button>
        {horizontalPunchcard}
        <Typography variant={'subtitle2'} textAlign={'center'} mb={1} mt={2}>
          Click a box to see more details
        </Typography>
        {trophyRequirements}
      </Container>
    </>
  );

  return fullScreen ? mobilePunchcard : punchcard;
};
